import { useState, useEffect } from 'react'

/**
 * Mobile breakpoints
 */
export const BREAKPOINTS = {
  mobile: 480,
  tablet: 768,
  desktop: 1024
}

/**
 * Hook to detect mobile/tablet devices and viewport size
 * @returns {Object} Mobile detection state
 */
export function useMobile() {
  const [state, setState] = useState(() => ({
    isMobile: typeof window !== 'undefined' ? window.innerWidth <= BREAKPOINTS.mobile : false,
    isTablet: typeof window !== 'undefined' ? window.innerWidth <= BREAKPOINTS.tablet && window.innerWidth > BREAKPOINTS.mobile : false,
    isTouch: typeof window !== 'undefined' ? ('ontouchstart' in window || navigator.maxTouchPoints > 0) : false,
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 768,
    isLandscape: typeof window !== 'undefined' ? window.innerWidth > window.innerHeight : true
  }))

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      const height = window.innerHeight

      setState({
        isMobile: width <= BREAKPOINTS.mobile,
        isTablet: width <= BREAKPOINTS.tablet && width > BREAKPOINTS.mobile,
        isTouch: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
        viewportWidth: width,
        viewportHeight: height,
        isLandscape: width > height
      })
    }

    // Set initial state
    handleResize()

    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
    }
  }, [])

  return {
    ...state,
    // Helper for responsive checks
    isMobileOrTablet: state.isMobile || state.isTablet,
    isDesktop: !state.isMobile && !state.isTablet
  }
}

/**
 * Hook to detect if device prefers reduced motion
 */
export function useReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)

    const handleChange = (e) => setPrefersReducedMotion(e.matches)
    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  return prefersReducedMotion
}

export default useMobile
